import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { Routes, Route, Link, useLocation, Outlet } from "react-router-dom";
import UserMenu from "./UserMenu";

import "./App.css";

import { Layout, Menu, Spin, message } from "antd";

import Dashboard from "./containers/Dashboard/Dashboard";
import Events from "./containers/Events/Events";
import Enterprises from "./containers/Enterprises/Enterprises";
import Surveys from "./containers/Surveys/Surveys";
import Owners from "./containers/Owners/Owners";
import DamageQuery from "./containers/Reports/DamageQuery";
import EventCostAnalysis from "./containers/Reports/EventCostAnalysis";
import { ENTERPRISE_TYPE } from "./Utils";

import { PieChartOutlined, DesktopOutlined, DatabaseOutlined, IdcardOutlined, AlertOutlined, IssuesCloseOutlined, BarChartOutlined } from '@ant-design/icons';

import 'font-awesome/css/font-awesome.min.css';

const { Header, Content, Footer, Sider } = Layout;

const AppContent = (props) => {
  const { t } = useTranslation();
  const location = useLocation();

  const [menuState, setMenuState] = useState({
    collapsed: false
  });

  const onCollapse = (collapsed) => {
    setMenuState({
      collapsed
    });
  };

  const key = "loadingMsg";

  useEffect(() => {
    if (props.loading) {
      message.info({ content: "Executing request", key, duration: 0 });
    } else {
      if (props.status) {
        switch (props.status) {
          case "success":
            message.success({
              content: "Success on execute request",
              key,
              duration: 5
            });
            break;
          case "failure":
            message.error({
              content: "Failed to execute request",
              key,
              duration: 5
            });
            break;
          default:
            message.warning({
              content: "Unexpected result on request!",
              key,
              duration: 5
            });
        }
      }
    }
  }, [props.loading, props.status]);

  // Generate items for sub-menus
  const enterpriseItems = [
    { key: "/enterprises/farms", icon: <DesktopOutlined />, label: <Link to="/enterprises/farms">{t("menu.farms")}</Link> },
    { key: "/enterprises/telecoms", icon: <DesktopOutlined />, label: <Link to="/enterprises/telecoms">{t("menu.telecommunications")}</Link> },
    { key: "/enterprises/electrics", icon: <DesktopOutlined />, label: <Link to="/enterprises/electrics">{t("menu.electrics")}</Link> },
    { key: "/enterprises/transport", icon: <DesktopOutlined />, label: <Link to="/enterprises/transport">{t("menu.transport")}</Link> },
    {
      key: "/enterprises/built-environments",
      icon: <DesktopOutlined />,
      label: t("menu.builtEnvironments"),
      children: [
        { key: "/enterprises/ordinary-building", label: <Link to="/enterprises/ordinary-building">{t("menu.ordinaryBuilding")}</Link> },
        { key: "/enterprises/non-ordinary-building", label: <Link to="/enterprises/non-ordinary-building">{t("menu.nonOrdinaryBuilding")}</Link> },
      ]
    },
  ];

  const surveyItems = [
    { key: "/surveys/farm", icon: <DesktopOutlined />, label: <Link to="/surveys/farm">{t("menu.surveysFarms")}</Link> },
    { key: "/surveys/telecom", icon: <DesktopOutlined />, label: <Link to="/surveys/telecom">{t("menu.surveysTelecommunications")}</Link> },
    { key: "/surveys/electric", icon: <DesktopOutlined />, label: <Link to="/surveys/electric">{t("menu.surveysElectrics")}</Link> },
    { key: "/surveys/transport", icon: <DesktopOutlined />, label: <Link to="/surveys/transport">{t("menu.surveysTransport")}</Link> },
    {
      key: "/surveys/built-environments",
      icon: <DesktopOutlined />,
      label: t("menu.builtEnvironments"),
      children: [
        { key: "/surveys/building", label: <Link to="/surveys/building">{t("menu.ordinaryBuilding")}</Link> },
        { key: "/surveys/non-ordinary-building", label: <Link to="/surveys/non-ordinary-building">{t("menu.nonOrdinaryBuilding")}</Link> },
      ]
    },
  ];

  const reportsItems = [
    { key: "/reports/damage-query", icon: <DesktopOutlined />, label: <Link to="/reports/damage-query">{t("menu.damageQuery")}</Link> },
    { key: "/reports/event-costs", icon: <DesktopOutlined />, label: <Link to="/reports/event-costs">{t("menu.eventCosts")}</Link> },
  ];

  // Create the items array for the Menu
  const items = [
    { key: "/dashboard", icon: <PieChartOutlined />, label: <Link to="/dashboard">{t("menu.dashboard")}</Link> },
    { key: "/events", icon: <AlertOutlined />, label: <Link to="/events">{t("menu.events")}</Link> },
    { key: "/owners", icon: <IdcardOutlined />, label: <Link to="/owners">{t("menu.owners")}</Link> },
    {
      key: "enterprises",
      icon: <DatabaseOutlined />,
      label: t("menu.enterprises"),
      children: enterpriseItems
    },
    {
      key: "surveys",
      icon: <IssuesCloseOutlined />,
      label: t("menu.surveys"),
      children: surveyItems
    },
    {
      key: "reports",
      icon: <BarChartOutlined />,
      label: t("menu.reports"),
      children: reportsItems
    },
  ];

  // Calculate open keys based on current path
  const defaultOpenKeys = [];
  const pathSegments = location.pathname.split('/');
  if (pathSegments.length > 1) {
    if (pathSegments[1] === 'enterprises') {
      defaultOpenKeys.push('enterprises');
      // Also open the "Built environments" submenu when looking at a building.
      if (pathSegments[2] === 'ordinary-building' || pathSegments[2] === 'non-ordinary-building') {
        defaultOpenKeys.push('/enterprises/built-environments');
      }
    }
    if (pathSegments[1] === 'surveys') {
      defaultOpenKeys.push('surveys');
      if (pathSegments[2] === 'building' || pathSegments[2] === 'non-ordinary-building') {
        defaultOpenKeys.push('/surveys/built-environments');
      }
    }
    if (pathSegments[1] === 'reports') defaultOpenKeys.push('reports');
  }

  return (
    <Spin size="large" spinning={props.loading}>
      <Layout style={{ minHeight: "100vh" }}>
        <Sider
          width="300"
          collapsible
          collapsed={menuState.collapsed}
          onCollapse={onCollapse}
        >
          <div className="logo" >
            <img src={`${process.env.PUBLIC_URL}/android-chrome-512x512.png`} alt="AHEAD Logo" width="50" />
          </div>
          <Menu
            theme="dark"
            defaultSelectedKeys={["/dashboard"]}
            mode="inline"
            selectedKeys={[location.pathname]}
            defaultOpenKeys={defaultOpenKeys}
            items={items}
          />
        </Sider>
        <Layout>
          <Header style={{
            background: '#001529',
            padding: 0,
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center'
          }}>
            <UserMenu />
          </Header>
          <Content
            style={{
              margin: "24px 16px",
              padding: 24,
              background: "#fff",
              minHeight: 280
            }}
          >
            <Outlet />
          </Content>
          <Footer style={{ textAlign: "center" }}>
            AHEAD ©2025 Created by PoliMi
          </Footer>
        </Layout>
      </Layout>
    </Spin>
  );
};

const mapStateToProps = state => {
  return {
    loading: state.loading,
    status: state.status
  };
};

// Connect AppContent to Redux
const ConnectedAppContent = connect(mapStateToProps)(AppContent);

// Main App component that sets up routes
const App = () => {
  return (
    <Routes>
      <Route path="/" element={<ConnectedAppContent />}>
        <Route index element={<Dashboard />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="events/*" element={<Events />} />
        <Route path="owners/*" element={<Owners />} />

        <Route path="enterprises/farms" element={<Enterprises key="farm-enterprise" type={ENTERPRISE_TYPE.FARM} />} />
        <Route path="enterprises/telecoms" element={<Enterprises key="telecom-enterprise" type={ENTERPRISE_TYPE.TLC} />} />
        <Route path="enterprises/electrics" element={<Enterprises key="electric-enterprise" type={ENTERPRISE_TYPE.ELECTRICAL} />} />
        <Route path="enterprises/transport" element={<Enterprises key="transport-enterprise" type={ENTERPRISE_TYPE.TRANSPORT} />} />
        <Route path="enterprises/ordinary-building" element={<Enterprises key="ordinary-building-enterprise" type={ENTERPRISE_TYPE.ORDINARY_BUILDING} />} />
        <Route path="enterprises/non-ordinary-building" element={<Enterprises key="non-ordinary-building-enterprise" type={ENTERPRISE_TYPE.NON_ORDINARY_BUILDING} />} />

        <Route path="surveys/farm" element={<Surveys key="farm-survey" type={ENTERPRISE_TYPE.FARM} />} />
        <Route path="surveys/telecom" element={<Surveys key="telecom-survey" type={ENTERPRISE_TYPE.TLC} />} />
        <Route path="surveys/electric" element={<Surveys key="electric-survey" type={ENTERPRISE_TYPE.ELECTRICAL} />} />
        <Route path="surveys/transport" element={<Surveys key="transport-survey" type={ENTERPRISE_TYPE.TRANSPORT} />} />
        <Route path="surveys/building" element={<Surveys key="building-survey" type={ENTERPRISE_TYPE.ORDINARY_BUILDING} />} />
        <Route path="surveys/non-ordinary-building" element={<Surveys key="non-ordinary-building-survey" type={ENTERPRISE_TYPE.NON_ORDINARY_BUILDING} />} />

        <Route path="reports/damage-query" element={<DamageQuery />} />
        <Route path="reports/event-costs" element={<EventCostAnalysis />} />
      </Route>
    </Routes>
  );
};

export default App;